/**
 * DigipartEnvironmentCard
 *
 * Admin card that displays and controls the DigiPART environment configuration
 * (staging / production / custom URL). Calls GET/PUT /api/admin/system-config/digipart/.
 *
 * Props:
 *   onSaved? — optional callback fired after a successful save
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe, Users, Clock, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { adminAPI } from '../services/api';
import { theme } from '../styles/theme';
import type { DigipartEnvironment, DigipartConfigResponse } from '../types';

interface DigipartEnvironmentCardProps {
  onSaved?: () => void;
}

const ENV_LABELS: Record<DigipartEnvironment, string> = {
  staging: 'Staging',
  production: 'Production',
  custom: 'Custom URL',
};

const ENV_COLORS: Record<DigipartEnvironment, string> = {
  staging: 'bg-qc-warning/10 text-qc-warning border border-qc-warning/30',
  production: 'bg-qc-success/10 text-qc-success border border-qc-success/30',
  custom: 'bg-bg-subtle text-text-secondary border border-border',
};

export const DigipartEnvironmentCard: React.FC<DigipartEnvironmentCardProps> = ({ onSaved }) => {
  const queryClient = useQueryClient();

  // ── local form state ──────────────────────────────────────────────────────
  const [selectedEnv, setSelectedEnv] = useState<DigipartEnvironment | null>(null);
  const [customUrl, setCustomUrl] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showWarning, setShowWarning] = useState(false);

  // ── server state ──────────────────────────────────────────────────────────
  const { data: config, isLoading } = useQuery<DigipartConfigResponse>({
    queryKey: ['admin', 'digipart-config'],
    queryFn: () => adminAPI.systemConfig.digipart.get().then((r) => r.data),
    staleTime: 30_000,
  });

  // Sync local selection to fetched data (first load only)
  React.useEffect(() => {
    if (config && selectedEnv === null) {
      setSelectedEnv(config.environment);
      setCustomUrl(config.custom_url ?? '');
    }
  }, [config, selectedEnv]);

  // ── mutation ──────────────────────────────────────────────────────────────
  const mutation = useMutation({
    mutationFn: () => {
      const payload = selectedEnv === 'custom'
        ? { environment: selectedEnv, custom_url: customUrl }
        : { environment: selectedEnv! };
      return adminAPI.systemConfig.digipart.update(payload).then((r) => r.data);
    },
    onSuccess: (updated: DigipartConfigResponse) => {
      queryClient.setQueryData(['admin', 'digipart-config'], updated);
      setSuccessMsg(`Environment saved: ${ENV_LABELS[updated.environment]}`);
      setErrorMsg(null);
      setShowWarning(false);
      onSaved?.();
      setTimeout(() => setSuccessMsg(null), 4000);
    },
    onError: (err: any) => {
      const detail =
        err?.response?.data?.environment?.[0] ||
        err?.response?.data?.custom_url?.[0] ||
        err?.response?.data?.non_field_errors?.[0] ||
        err?.response?.data?.detail ||
        'Failed to save configuration.';
      setErrorMsg(detail);
      setSuccessMsg(null);
    },
  });

  // ── handlers ──────────────────────────────────────────────────────────────
  const handleEnvChange = (env: DigipartEnvironment) => {
    setSelectedEnv(env);
    setErrorMsg(null);
    setSuccessMsg(null);
    // Warn about session invalidation if changing away from current
    if (config && env !== config.environment && config.active_sessions > 0) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  };

  const handleSave = () => {
    if (!selectedEnv) return;
    if (selectedEnv === 'custom' && !customUrl.startsWith('https://')) {
      setErrorMsg('Custom URL must start with https://');
      return;
    }
    mutation.mutate();
  };

  const isDirty = config
    ? selectedEnv !== config.environment ||
      (selectedEnv === 'custom' && customUrl !== (config.custom_url ?? ''))
    : false;

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className={`${theme.card.background} ${theme.card.border} border rounded-lg shadow p-6`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h2 className={`text-lg font-semibold ${theme.text.primary} flex items-center gap-2`}>
          <Globe className="w-5 h-5 text-interactive-primary" />
          DigiPART Environment
        </h2>
        {isLoading && <Loader2 className="w-4 h-4 animate-spin text-text-tertiary" />}
        {config && !isLoading && (
          <span
            className={`text-xs font-medium px-2.5 py-1 rounded-full ${ENV_COLORS[config.environment]}`}
            data-testid="digipart-env-badge"
          >
            {ENV_LABELS[config.environment]}
          </span>
        )}
      </div>

      {/* Current resolved URL */}
      {config && (
        <div className={`mb-4 p-3 rounded-md ${theme.background.secondary} text-sm`}>
          <span className={`${theme.text.secondary} font-medium mr-2`}>Active URL:</span>
          <span
            className={`${theme.text.primary} font-mono break-all`}
            data-testid="digipart-resolved-url"
          >
            {config.resolved_url}
          </span>
        </div>
      )}

      {/* Session + audit info */}
      {config && (
        <div className={`flex gap-4 mb-5 text-xs ${theme.text.secondary}`}>
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            <span data-testid="digipart-session-count">
              {config.active_sessions} active session{config.active_sessions !== 1 ? 's' : ''}
            </span>
          </span>
          {config.last_changed_by && (
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Last changed by {config.last_changed_by}
            </span>
          )}
        </div>
      )}

      {/* Environment selector */}
      <div className="space-y-2 mb-4">
        <p className={`text-sm font-medium ${theme.text.secondary} mb-2`}>Select Environment</p>
        {(['staging', 'production', 'custom'] as DigipartEnvironment[]).map((env) => (
          <label
            key={env}
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              selectedEnv === env
                ? 'border-interactive-primary bg-interactive-primary/10'
                : `${theme.card.border} ${theme.card.background} hover:${theme.background.secondary}`
            }`}
            data-testid={`digipart-env-option-${env}`}
          >
            <input
              type="radio"
              name="digipart-environment"
              value={env}
              checked={selectedEnv === env}
              onChange={() => handleEnvChange(env)}
              className="accent-interactive-primary"
            />
            <div>
              <span className={`text-sm font-medium ${theme.text.primary}`}>{ENV_LABELS[env]}</span>
              {env === 'staging' && (
                <p className={`text-xs ${theme.text.tertiary}`}>
                  https://digipart-test.spare-parts-3d.com
                </p>
              )}
              {env === 'production' && (
                <p className={`text-xs ${theme.text.tertiary}`}>
                  https://digipart.spare-parts-3d.com
                </p>
              )}
              {env === 'custom' && (
                <p className={`text-xs ${theme.text.tertiary}`}>
                  Specify your own DigiPART endpoint
                </p>
              )}
            </div>
          </label>
        ))}
      </div>

      {/* Custom URL input — shown only when custom is selected */}
      {selectedEnv === 'custom' && (
        <div className="mb-4">
          <label className={`block text-sm font-medium ${theme.text.secondary} mb-1`}>
            Custom URL <span className="text-text-tertiary" aria-hidden="true">*</span>
          </label>
          <input
            type="url"
            value={customUrl}
            onChange={(e) => {
              setCustomUrl(e.target.value);
              setErrorMsg(null);
            }}
            placeholder="https://your-digipart.example.com"
            className={`w-full px-3 py-2 text-sm rounded-md border ${theme.card.border} ${theme.background.secondary} ${theme.text.primary} placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-interactive-primary font-mono`}
            data-testid="digipart-custom-url-input"
          />
          <p className={`mt-1 text-xs ${theme.text.tertiary}`}>Must start with https://</p>
        </div>
      )}

      {/* Session invalidation warning */}
      {showWarning && config && config.active_sessions > 0 && (
        <div className="flex items-start gap-2 p-3 mb-4 rounded-md bg-qc-warning/10 border border-qc-warning/30">
          <AlertTriangle className="w-4 h-4 text-qc-warning shrink-0 mt-0.5" />
          <p className="text-sm text-qc-warning">
            Switching environment will invalidate{' '}
            <strong>{config.active_sessions} active DigiPART session{config.active_sessions !== 1 ? 's' : ''}</strong>.
            Users will need to re-connect.
          </p>
        </div>
      )}

      {/* Error message */}
      {errorMsg && (
        <div className="flex items-start gap-2 p-3 mb-4 rounded-md bg-qc-error/10 border border-qc-error/30">
          <AlertTriangle className="w-4 h-4 text-qc-error shrink-0 mt-0.5" />
          <p className="text-sm text-qc-error" data-testid="digipart-error-msg">
            {errorMsg}
          </p>
        </div>
      )}

      {/* Success message */}
      {successMsg && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-qc-success/10 border border-qc-success/30">
          <CheckCircle2 className="w-4 h-4 text-qc-success shrink-0" />
          <p className="text-sm text-qc-success" data-testid="digipart-success-msg">
            {successMsg}
          </p>
        </div>
      )}

      {/* Save button */}
      <button
        onClick={handleSave}
        disabled={!isDirty || mutation.isPending || isLoading}
        className="w-full flex items-center justify-center gap-2 bg-interactive-primary hover:bg-interactive-primary-hover disabled:bg-interactive-primary text-text-inverse font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
        data-testid="digipart-save-btn"
      >
        {mutation.isPending ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Saving…
          </>
        ) : (
          'Save Environment'
        )}
      </button>
    </div>
  );
};

export default DigipartEnvironmentCard;
